<template>
  <Modal :is-open="true" @close="$emit('cancel')">
    <template #header>
      <h2 class="text-xl font-semibold">
        {{ transaction ? 'Edit Fuel Transaction' : 'Add Fuel Transaction' }}
      </h2>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <!-- Equipment Selection -->
      <FuelEquipmentSelector v-model="formData.equipment_id" ref="equipmentSelector" />

      <!-- Liters and Cost -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Liters <span class="text-red-500">*</span>
          </label>
          <input
            type="number"
            v-model.number="formData.liters"
            step="0.01"
            min="0.01"
            required
            class="w-full p-2 border border-gray-300 rounded-md"
            :class="{ 'border-red-500': errors.liters }"
            placeholder="0.00"
          />
          <p v-if="errors.liters" class="text-red-500 text-xs mt-1">{{ errors.liters }}</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Cost per Liter (tsh) <span class="text-red-500">*</span>
          </label>
          <input
            type="number"
            v-model.number="formData.cost_per_liter"
            step="0.01"
            min="0.01"
            required
            class="w-full p-2 border border-gray-300 rounded-md"
            :class="{ 'border-red-500': errors.cost_per_liter }"
            placeholder="0.00"
          />
          <p v-if="errors.cost_per_liter" class="text-red-500 text-xs mt-1">
            {{ errors.cost_per_liter }}
          </p>
        </div>
      </div>

      <!-- Total Cost Calculation -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div class="flex justify-between items-center">
          <span class="text-sm font-medium text-blue-800">Total Cost:</span>
          <span class="text-lg font-bold text-blue-800"> tsh{{ totalCost.toFixed(2) }} </span>
        </div>
        <p class="text-xs text-blue-600 mt-1">Calculated automatically (Liters × Cost per Liter)</p>
      </div>

      <!-- Transaction Date -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Transaction Date <span class="text-red-500">*</span>
        </label>
        <input
          type="datetime-local"
          v-model="formData.transaction_date"
          required
          class="w-full p-2 border border-gray-300 rounded-md"
          :class="{ 'border-red-500': errors.transaction_date }"
        />
        <p v-if="errors.transaction_date" class="text-red-500 text-xs mt-1">
          {{ errors.transaction_date }}
        </p>
      </div>

      <!-- Payment Method -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Source of Money (Payment Method) <span class="text-red-500">*</span>
        </label>
        <select
          v-model="formData.payment_method"
          required
          class="w-full p-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="CASH">💵 Cash Money</option>
          <option value="LIPA_NAMBA">📱 Company Lipa Namba</option>
          <option value="BANK_ACCOUNT">🏦 Company Bank Account</option>
        </select>
        <p class="text-xs text-gray-500 mt-1">Specify which company funding source was used for this expenditure.</p>
      </div>

      <!-- Notes -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          v-model="formData.notes"
          rows="3"
          class="w-full p-2 border border-gray-300 rounded-md"
          placeholder="Additional notes about this fuel transaction..."
        ></textarea>
      </div>

      <!-- Form Actions -->
      <div class="flex space-x-3 pt-4">
        <button type="submit" class="btn-primary flex-1" :disabled="loading">
          {{ loading ? 'Saving...' : transaction ? 'Update' : 'Add Fuel' }}
        </button>
        <button type="button" @click="$emit('cancel')" class="btn-secondary flex-1">Cancel</button>
      </div>
    </form>
  </Modal>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import type { FuelTransaction, FuelTransactionCreate } from '@/types/fuel'
import Modal from '@/components/shared/Modal.vue'
import FuelEquipmentSelector from './FuelEquipmentSelector.vue'

interface Props {
  transaction?: FuelTransaction
  loading?: boolean
}

interface Errors {
  equipment_id?: string
  liters?: string
  cost_per_liter?: string
  transaction_date?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'save', data: FuelTransactionCreate): void
  (e: 'cancel'): void
}>()

const equipmentSelector = ref<InstanceType<typeof FuelEquipmentSelector>>()

const formData = reactive<FuelTransactionCreate>({
  equipment_id: undefined,
  liters: undefined,
  cost_per_liter: undefined,
  payment_method: 'CASH',
  transaction_date: new Date().toISOString().slice(0, 16),
  notes: undefined,
})

const errors = reactive<Errors>({})

// Calculate total cost
const totalCost = computed(() => {
  return (formData.liters || 0) * (formData.cost_per_liter || 0)
})

// Populate form if editing existing transaction
watch(
  () => props.transaction,
  (transaction) => {
    if (transaction) {
      Object.assign(formData, {
        equipment_id: transaction.equipment_id,
        liters: transaction.liters,
        cost_per_liter: transaction.cost_per_liter,
        payment_method: transaction.payment_method || 'CASH',
        transaction_date: transaction.transaction_date.slice(0, 16),
        notes: transaction.notes,
      })
      // Ensure the equipment selector highlights the correct equipment
      try {
        ;(
          equipmentSelector.value as unknown as { setSelected?: (id?: number) => void }
        )?.setSelected?.(transaction.equipment_id)
      } catch {
        // ignore if ref not yet available
      }
    }
  },
  { immediate: true },
)

const validateForm = (): boolean => {
  const newErrors: Errors = {}

  // Validate equipment selection
  if (!equipmentSelector.value?.validate()) {
    newErrors.equipment_id = 'Equipment selection is required'
  }

  // Also ensure formData.equipment_id is a positive integer
  if (!formData.equipment_id || Number(formData.equipment_id) <= 0) {
    newErrors.equipment_id = 'Equipment selection is required'
  }

  if (!formData.liters || formData.liters <= 0) {
    newErrors.liters = 'Please enter a valid amount of liters'
  }

  if (!formData.cost_per_liter || formData.cost_per_liter <= 0) {
    newErrors.cost_per_liter = 'Please enter a valid cost per liter'
  }

  if (!formData.transaction_date) {
    newErrors.transaction_date = 'Transaction date is required'
  }

  Object.assign(errors, newErrors)
  return Object.keys(newErrors).length === 0
}

const handleSubmit = () => {
  if (validateForm()) {
    // Convert datetime-local to ISO format
    const isoDate = new Date(formData.transaction_date).toISOString()

    // Build payload with only allowed fields (do not send server-computed `total_cost`)
    const transactionData: FuelTransactionCreate = {
      equipment_id: Number(formData.equipment_id),
      liters: Number(formData.liters),
      cost_per_liter: Number(formData.cost_per_liter),
      payment_method: formData.payment_method || 'CASH',
      transaction_date: isoDate,
      notes: formData.notes,
    }

    // Debug: log exact payload being emitted
    try {
      console.debug('[FuelTransactionForm] emitting payload', JSON.stringify(transactionData))
    } catch {}

    emit('save', transactionData)
  }
}
</script>
